import { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { Trash2, Image as ImageIcon, Link as LinkIcon } from "lucide-react";

/* ================= PAGE ================= */
export default function AdminGallery() {
  const [imageUrl, setImageUrl] = useState("");
  const [description, setDescription] = useState("");
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dark, setDark] = useState(true);

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const loadTheme = () => {
      const t = localStorage.getItem("theme");
      setDark(t !== "light");
    };

    loadTheme();
    window.addEventListener("theme-change", loadTheme);
    return () => window.removeEventListener("theme-change", loadTheme);
  }, []);

  /* ================= LOAD GALLERY ================= */
  const loadGallery = async () => {
    const snap = await getDocs(collection(db, "gallery"));
    setGallery(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    loadGallery();
  }, []);

  /* ================= URL VALIDATION ================= */
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  /* ================= ADD IMAGE LINK ================= */
  const addImageLink = async () => {
    if (!imageUrl) return alert("Paste image URL");
    if (!isValidUrl(imageUrl)) return alert("Invalid image URL");

    setLoading(true);
    try {
      await addDoc(collection(db, "gallery"), {
        imageUrl,
        description,
        createdAt: serverTimestamp(),
      });

      setImageUrl("");
      setDescription("");
      loadGallery();
    } catch (err) {
      console.error(err);
      alert("Failed to save image");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DELETE IMAGE ================= */
  const deleteImage = async (item) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await deleteDoc(doc(db, "gallery", item.id));
      loadGallery();
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  /* ================= THEME MAP ================= */
  const theme = {
    page: dark ? "bg-[#0b0b0b] text-white" : "bg-gray-100 text-gray-900",
    card: dark ? "bg-white/5 border-white/10" : "bg-white border-gray-200",
    input: dark
      ? "bg-black/40 border-white/10 text-white"
      : "bg-white border-gray-300 text-gray-900",
    muted: dark ? "text-gray-400" : "text-gray-600",
    border: dark ? "border-white/10" : "border-gray-200",
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${theme.page}`}>
      <div className="max-w-6xl mx-auto p-6">
        {/* ================= HEADER ================= */}
        <div className="mb-10">
          <h2 className="text-3xl font-bold mb-1">
            Gallery Management
          </h2>
          <p className={`text-sm ${theme.muted}`}>
            Manage public gallery images using external image links
          </p>
        </div>

        {/* ================= ADD IMAGE ================= */}
        <div
          className={`rounded-2xl border shadow-sm p-6 mb-12 ${theme.card}`}
        >
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <LinkIcon size={18} /> Add Image via URL
          </h3>

          <input
            type="url"
            placeholder="https://example.com/image.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            className={`w-full rounded-xl border px-4 py-3 mb-4 transition ${theme.input}`}
          />

          <textarea
            placeholder="Image description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`w-full rounded-xl border px-4 py-3 mb-4 transition ${theme.input}`}
          />

          <button
            onClick={addImageLink}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-sky-600 text-white font-semibold hover:bg-sky-700 transition"
          >
            <ImageIcon size={16} />
            {loading ? "Saving..." : "Save Image"}
          </button>
        </div>

        {/* ================= GALLERY GRID ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {gallery.map((item) => (
            <div
              key={item.id}
              className={`relative group rounded-2xl overflow-hidden border shadow-sm transition hover:shadow-lg ${theme.border}`}
            >
              <img
                src={item.imageUrl}
                alt={item.description || "Gallery image"}
                className="w-full h-56 object-cover"
                loading="lazy"
              />

              {/* OVERLAY */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col justify-between p-4">
                <p className="text-white text-sm">
                  {item.description || "No description"}
                </p>

                <button
                  onClick={() => deleteImage(item)}
                  className="self-end bg-red-600 text-white p-2 rounded-lg hover:bg-red-700"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ================= EMPTY ================= */}
        {gallery.length === 0 && (
          <p className={`mt-10 text-center ${theme.muted}`}>
            No gallery images added.
          </p>
        )}
      </div>
    </div>
  );
}
